/**
 * getUserMedia wrangling for the selfie step (SPEC §6).
 *
 * Every failure mode here has the same answer — fall back to camera-roll upload,
 * which is offered as an equal alternative anyway — so this module's job is to
 * classify the failure well enough to write an honest sentence about it.
 */

export type CameraProblem =
  /** getUserMedia only exists in a secure context. HTTPS, or localhost. */
  | 'insecure-context'
  /** The API is missing entirely (old browser, in-app webview). */
  | 'unsupported'
  /** The user said no, or the OS/browser has it blocked. */
  | 'denied'
  /** There is no camera on this device. */
  | 'no-camera'
  /** Something else has the camera open. */
  | 'in-use'
  | 'unknown';

export class CameraError extends Error {
  readonly problem: CameraProblem;

  constructor(problem: CameraProblem, message: string) {
    super(message);
    this.name = 'CameraError';
    this.problem = problem;
  }
}

const MESSAGES: Readonly<Record<CameraProblem, string>> = {
  'insecure-context':
    'The camera needs a secure connection (https). Upload a photo from your camera roll instead.',
  unsupported: "This browser won't give us the camera. Upload a photo instead.",
  denied: 'Camera access was blocked. You can allow it in your browser settings, or upload a photo.',
  'no-camera': "We couldn't find a camera on this device. Upload a photo instead.",
  'in-use': 'Something else is using the camera. Close it and try again, or upload a photo.',
  unknown: "The camera didn't start. Upload a photo instead.",
};

export function cameraMessage(problem: CameraProblem): string {
  return MESSAGES[problem];
}

/**
 * `http://localhost` counts as a secure context by definition, which is why
 * desktop dev works without TLS but a real phone needs `wrangler dev
 * --local-protocol https` or a tunnel (PLAN.md Wave 3).
 */
export function isSecureContextForCamera(): boolean {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext === true;
}

export function cameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

/** Why the camera cannot even be attempted, or null when it can. */
export function preflightCamera(): CameraProblem | null {
  if (!cameraSupported()) {
    return isSecureContextForCamera() ? 'unsupported' : 'insecure-context';
  }
  if (!isSecureContextForCamera()) return 'insecure-context';
  return null;
}

/** Front camera, roughly portrait, sized generously — we downscale on capture. */
const CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: 'user',
    width: { ideal: 1080 },
    height: { ideal: 1350 },
  },
};

/** Open the front camera. Throws a classified `CameraError` on any failure. */
export async function openCamera(): Promise<MediaStream> {
  const blocked = preflightCamera();
  if (blocked) throw new CameraError(blocked, cameraMessage(blocked));

  try {
    return await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
  } catch (error) {
    const problem = classify(error);
    throw new CameraError(problem, cameraMessage(problem));
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      /* already stopped */
    }
  }
}

/**
 * Grab the current video frame as a canvas, ready for `processPhoto`.
 *
 * Mirrored by default, because the live preview is: an un-mirrored frame is not
 * the picture the player just posed for, and — since they go on to drag a crop
 * window over it — it would also put their face on the wrong side of the box.
 */
export function grabFrame(video: HTMLVideoElement, { mirror = true } = {}): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || 720;
  canvas.height = video.videoHeight || 900;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new CameraError('unknown', cameraMessage('unknown'));
  if (mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function classify(error: unknown): CameraProblem {
  const name = (error as { name?: string } | null)?.name ?? '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'no-camera';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'in-use';
    default:
      return 'unknown';
  }
}
